import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, Sparkles, ArrowRight, Bookmark, RotateCcw } from 'lucide-react';

// Split the formulation prose into scannable sentence bullets.
// The AI writes it as "Ingredient X sits/appears/is ... , which means ..." —
// almost one observation per sentence — so sentence-splitting yields a clean
// list. The leading ingredient phrase (capitalised words before a verb) gets
// bolded for structure. Abbreviations like "e.g." are protected from the split.
function formulationBullets(text) {
  if (!text || typeof text !== 'string') return [];
  const protectedText = text
    .replace(/e\.g\./gi, 'e·g·')
    .replace(/i\.e\./gi, 'i·e·')
    .replace(/vs\./gi, 'vs·');
  const sentences = protectedText
    .split(/(?<=\.)\s+(?=[A-Z(])/)
    .map(s => s.replace(/e·g·/g, 'e.g.').replace(/i·e·/g, 'i.e.').replace(/vs·/g, 'vs.').trim())
    .filter(Boolean);
  return sentences.map(sentence => {
    // Bold a leading ingredient-looking phrase: Capitalised word(s) followed by
    // a descriptive verb. Falls back to no bold when the pattern doesn't match.
    const m = sentence.match(/^([A-Z][a-zA-Z-]+(?:\s+[A-Z(][a-zA-Z)-]+)*)\s+(sits|appears|is|are|adds|provides|comes|shows up|features|anchors)\b/);
    if (m) {
      return { lead: m[1], rest: sentence.slice(m[1].length) };
    }
    return { lead: null, rest: sentence };
  });
}

const ProductAnalysisCard = ({ analysis, productName, productBrand, productImage, onSave, customListNames = {}, onAnalyseAnother, onDone, onSeeFullAnalysis }) => {
  const [savedTo, setSavedTo] = useState(null);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);

  // Circular progress ring
  const score = analysis.score || 0;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (score / 100) * circumference;

  const getRatingLabel = (rating) => {
    const map = { excellent: 'Excellent', good: 'Good', fair: 'Fair', poor: 'Poor' };
    return map[rating] || (rating ? rating.charAt(0).toUpperCase() + rating.slice(1) : '');
  };

  const getRatingBadgeStyle = (rating) => {
    if (rating === 'excellent' || rating === 'good') return 'text-green-700 border border-green-200 bg-white';
    if (rating === 'fair') return 'text-yellow-700 border border-yellow-200 bg-white';
    return 'text-red-700 border border-red-200 bg-white';
  };

  const getVerdictLabel = (verdict) => {
    if (verdict === 'highly-recommended') return 'Highly Recommended';
    if (verdict === 'recommended') return 'Recommended';
    if (verdict === 'proceed-with-caution') return 'Proceed with Caution';
    return 'Not Recommended';
  };

  const isPositiveVerdict = (
    analysis.recommendation?.verdict === 'highly-recommended' ||
    analysis.recommendation?.verdict === 'recommended'
  );

  const handleSave = (category) => {
    const product = {
      image: productImage,
      name: productName,
      brand: productBrand,
      savedAt: new Date().toISOString(),
      ai_analysis: JSON.stringify({
        score: analysis.score,
        compatibility: analysis.compatibility,
        reasoning: analysis.reasoning,
        concerns: analysis.concerns || [],
        pros: analysis.whatsGreat || [],
        best_for: analysis.recommendation?.usage || 'General use',
        ingredients_highlight: analysis.ingredients_highlight || '',
        // #55: benefit/attribute chips + structured ingredients (graceful until enriched).
        benefits: analysis.benefits || [],
        ingredients: analysis.ingredients || [],
        finish: analysis.finish || null,
        coverage: analysis.coverage || null,
        spf: analysis.spf || null,
        key_ingredients: analysis.keyIngredients || analysis.key_ingredients || []
      })
    };
    if (onSave) onSave(product, category);
    setSavedTo(category);
    setShowSaveDropdown(false);
  };

  // Parse ingredients into a flat array regardless of source format
  const ingredients = analysis.keyIngredients
    ? analysis.keyIngredients
    : analysis.ingredients_highlight
      ? analysis.ingredients_highlight.split(/(?<!\d)[,;](?!\d)\s*/).map(i => i.trim()).filter(Boolean)
      : [];

  const scoreLabel =
    score >= 85 ? 'Excellent compatibility with your skin profile.' :
    score >= 70 ? 'Good compatibility with your skin profile.' :
    score >= 50 ? 'Moderate compatibility with your skin profile.' :
                  'Limited compatibility with your skin profile.';

  return (
    <div className="space-y-3 max-w-2xl mx-auto pb-4">

      {/* ── Product image card ── */}
      <div className="rounded-2xl overflow-hidden bg-white border border-[#341C1C]/8 shadow-sm">
        {productImage ? (
          <div className="relative">
            <img
              src={productImage}
              alt={productName || 'Product'}
              className="w-full h-52 object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2035]/70 via-transparent to-transparent" />
            {/* Category badge */}
            {analysis.productType && (
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#F3D5B5] text-[#2A324B] text-xs font-medium rounded-full">
                {analysis.productType}
              </span>
            )}
            {/* Name + brand overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="font-semibold text-white text-base leading-tight">{productName || 'Unknown Product'}</h3>
              {productBrand && <p className="text-white/70 text-sm mt-0.5">{productBrand}</p>}
            </div>
          </div>
        ) : (
          <div className="p-4 pb-0">
            {analysis.productType && (
              <span className="inline-block px-2.5 py-1 bg-[#F3D5B5] text-[#2A324B] text-xs font-medium rounded-full mb-2">
                {analysis.productType}
              </span>
            )}
            <h3 className="font-semibold text-[#2A324B] text-base">{productName || 'Unknown Product'}</h3>
            {productBrand && <p className="text-[#2A324B]/60 text-sm">{productBrand}</p>}
          </div>
        )}

        {/* Key ingredients + price */}
        <div className="p-4">
          {(ingredients.length > 0 || analysis.ingredient_insight) && (
            <div className="mb-3">
              {ingredients.length > 0 && (
                <>
                  <p className="text-[13px] font-semibold tracking-widest text-[#2A324B]/40 uppercase mb-2">
                    Key Ingredients
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {ingredients.map((ingredient, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 text-xs text-[#2A324B] border border-[#2A324B]/15 rounded-full bg-white"
                      >
                        {ingredient}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {analysis.ingredient_insight && (
                <div className={`${ingredients.length > 0 ? 'mt-3' : ''} p-4 bg-[#F2EAE1]/60 rounded-lg border border-[#F3D5B5]/40`}>
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#2A324B]/45 flex-shrink-0" />
                    <p className="text-[13px] font-semibold tracking-widest text-[#2A324B]/45 uppercase">Understanding the Formulation</p>
                  </div>
                  <ul className="space-y-2.5">
                    {formulationBullets(analysis.ingredient_insight).map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C8956A] flex-shrink-0 mt-[9px]" />
                        <p className="text-[16px] text-[#2A324B]/85 leading-[1.6] flex-1">
                          {b.lead && <span className="font-semibold text-[#2A324B]">{b.lead}</span>}
                          {b.rest}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {analysis.price && (
            <p className="text-base font-semibold text-[#2A324B]">{analysis.price}</p>
          )}
        </div>
      </div>

      {/* ── Match score — dark card ── */}
      <div className="rounded-2xl bg-[#2A324B] p-5 flex items-center gap-5">
        {/* SVG circular ring */}
        <div className="relative flex-shrink-0">
          <svg width="90" height="90" className="-rotate-90" aria-hidden>
            <circle
              cx="45" cy="45" r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="6"
            />
            <circle
              cx="45" cy="45" r={radius}
              fill="none"
              stroke="#F3D5B5"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeOffset}
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-bold text-white">{score}%</span>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white text-base">Your Match Score</h4>
          <p className="text-white/55 text-xs mt-0.5 leading-snug">{scoreLabel}</p>
          <p className="text-[#F3D5B5]/65 text-[13px] mt-2">🔥 Powered by Son:mi</p>
        </div>
      </div>

      {/* ── Compatibility breakdown ── */}
      {(analysis.skinTypeMatch || analysis.skinToneMatch || analysis.concernsMatch) && (
        <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
          <h4 className="text-sm font-semibold text-[#2A324B] mb-3">Compatibility Breakdown</h4>
          <div className="space-y-3">
            {analysis.skinTypeMatch && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#2A324B]">Skin Type</span>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${getRatingBadgeStyle(analysis.skinTypeMatch)}`}>
                  <CheckCircle className="h-3 w-3" />
                  {getRatingLabel(analysis.skinTypeMatch)}
                </span>
              </div>
            )}
            {analysis.skinToneMatch && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#2A324B]">Skin Tone</span>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${getRatingBadgeStyle(analysis.skinToneMatch)}`}>
                  <CheckCircle className="h-3 w-3" />
                  {getRatingLabel(analysis.skinToneMatch)}
                </span>
              </div>
            )}
            {analysis.concernsMatch && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-sm text-[#2A324B]">Your Concerns</span>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${getRatingBadgeStyle(analysis.concernsMatch)}`}>
                  <CheckCircle className="h-3 w-3" />
                  {getRatingLabel(analysis.concernsMatch)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── What's great for you ── */}
      {analysis.whatsGreat && analysis.whatsGreat.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <h4 className="text-sm font-semibold text-[#2A324B]">What's great for you</h4>
          </div>
          <div className="space-y-2.5">
            {analysis.whatsGreat.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="mt-0.5 w-4 h-4 rounded-full border-2 border-green-400 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                </div>
                <span className="text-sm text-[#2A324B]/80 leading-snug">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Things to know ── */}
      {analysis.concerns && analysis.concerns.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h4 className="text-sm font-semibold text-[#2A324B]">Things to know</h4>
          </div>
          <div className="space-y-2.5">
            {analysis.concerns.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="mt-0.5 w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                </div>
                <span className="text-sm text-[#2A324B]/80 leading-snug">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verdict card removed — score + compatibility breakdown carry this information */}

      {/* ── Alternatives to consider ── */}
      {analysis.alternatives && analysis.alternatives.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#341C1C]/8 shadow-sm p-4">
          <h4 className="text-sm font-semibold text-[#2A324B] mb-3">Alternatives to consider</h4>
          <div className="space-y-2.5">
            {analysis.alternatives.map((alt, idx) => (
              <div key={idx} className="flex items-start gap-2.5">
                <ArrowRight className="h-3.5 w-3.5 text-[#2A324B]/35 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-[#2A324B]/75 leading-snug">{alt}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── See full formula analysis — opens the rich menu view (only when wired).
          Full-width but outlined (no fill) so it reads as a real CTA without adding
          another heavy filled band to the turn. ── */}
      {onSeeFullAnalysis && (
        <button
          onClick={onSeeFullAnalysis}
          className="w-full flex items-center justify-between gap-2 rounded-[13px] border border-[#E1CDB2] px-[15px] py-3 text-left hover:bg-[#FBF4EA] transition-colors"
        >
          <span className="text-sm font-medium text-[#8A5A2B]">See full formula analysis</span>
          <ArrowRight className="h-[18px] w-[18px] text-[#8A5A2B] flex-shrink-0" />
        </button>
      )}

      {/* ── Save to collection ── */}
      <div className="rounded-2xl bg-[#F3D5B5]/20 border border-[#F3D5B5]/50 p-4">
        <p className="text-sm font-medium text-[#2A324B] mb-3">Save to your collection</p>
        {savedTo ? (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full border border-green-200">
            <Bookmark className="h-3.5 w-3.5 text-green-600 fill-current" />
            <span className="text-green-700 text-sm font-medium">
              Saved to{' '}
              {savedTo === 'current' ? 'Currently Using' :
               savedTo === 'wishlist' ? 'Want to Buy' :
               savedTo === 'custom1' ? customListNames.custom1 :
               savedTo === 'custom2' ? customListNames.custom2 : 'List'}
            </span>
          </div>
        ) : (
          <div className="relative inline-block">
            <button
              onClick={() => setShowSaveDropdown(!showSaveDropdown)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2A324B]/20 bg-white text-[#2A324B] text-sm font-medium hover:bg-[#F2EAE1] transition-colors"
            >
              <Bookmark className="h-4 w-4" />
              Save to Catalogue
            </button>
            {showSaveDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSaveDropdown(false)} />
                <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-lg border border-[#341C1C]/10 z-50 overflow-hidden min-w-[180px]">
                  <button
                    onClick={() => handleSave('current')}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F2EAE1] text-sm text-[#2A324B] transition-colors"
                  >
                    Currently Using
                  </button>
                  <button
                    onClick={() => handleSave('wishlist')}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F2EAE1] text-sm text-[#2A324B] transition-colors"
                  >
                    Want to Buy
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Bottom action buttons (optional — shown only when handlers are provided) ── */}
      {(onAnalyseAnother || onDone) && (
        <div className="flex gap-3 pt-1">
          {onAnalyseAnother && (
            <button
              onClick={onAnalyseAnother}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full border border-[#2A324B]/20 bg-white text-[#2A324B] text-sm font-medium hover:bg-[#F2EAE1] transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Analyse Another
            </button>
          )}
          {onDone && (
            <button
              onClick={onDone}
              className="flex-1 py-3 rounded-full bg-[#2A324B] text-[#F3D5B5] text-sm font-medium hover:bg-[#2A324B]/90 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAnalysisCard;
